import moment from 'moment'

// Categorical palette: the validated 8-slot order. Verified against the #ffffff
// chart surface -- lightness band, chroma floor, CVD separation and normal-vision
// floor all pass. Three slots (aqua, yellow, magenta) sit below 3:1 contrast, so
// the relief rule applies: every chart using them ships a table view.
//
// Slots are assigned by entity identity and never cycled. A 9th series folds into
// OTHER rather than generating a new hue, which would be indistinguishable under
// colour-vision deficiency.
export const PALETTE = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948']
export const OTHER_COLOR = '#898781'

const DEFAULT_SERIES_KEY = (row) => row.action

/**
 * Sentinel exclude-user "id" standing in for "no actor at all" (`userId ===
 * null`, labelled "System" -- see the three-population docstring in
 * `activity/query.py`). Not a real `auth_user` pk -- a row with no actor has
 * no id to exclude by, so this constant is the value the System option (in
 * `useActivityReport`'s `excludeUserOptions`) and `filterRows` below agree to
 * treat as that row's "id". Never collides with a real id: ids arriving over
 * the wire are numeric, this is not.
 */
export const SYSTEM_EXCLUDE_VALUE = 'system'

/**
 * The exclude-users filter's default value: System (no actor at all) and the
 * `cloud_functions` service account, both filtered out before the user has
 * touched anything.
 *
 * A static constant, not something seeded into filter state by an effect once
 * rows arrive. An effect would race the first fetch -- either it runs before
 * rows exist and has nothing to seed, or it re-runs whenever rows change
 * (a new date range, a report switch) and silently re-adds a token the user
 * just removed. A static default has neither problem: `filterRows` can act on
 * it from the very first render, whether or not any row has loaded yet, and
 * once a caller removes an entry from its own filter *state*, nothing in this
 * module regenerates it -- the default is only ever the starting value, never
 * reasserted.
 *
 * `cloud_functions`'s entry sets `matchBy: 'username'`, the one deliberate,
 * documented exception to "match a user by id, never by display name" (see
 * `filterRows` below): it is not a display-name match. The account's
 * `auth_user` pk differs by environment, so hardcoding an id would work only
 * where it was written and silently exclude nothing (or the wrong user)
 * everywhere else. Its *display* name is "GCP Functions" -- the same
 * first/last-name formula every other row goes through (see
 * `activity/query.py`) -- which is not what "cloud_functions" means and
 * would collide with a real person coincidentally named that. `username` is
 * the one identifier that is both stable across environments and unambiguous.
 * The token's label spells out both names, since a viewer who only knows the
 * account as "cloud_functions" would not otherwise connect it to "GCP
 * Functions" elsewhere in the same UI (the plain per-user exclude option,
 * keyed by id, is labelled "GCP Functions" like every other row).
 */
export const DEFAULT_EXCLUDED_USERS = [
    { value: SYSTEM_EXCLUDE_VALUE, label: 'System' },
    { value: 'cloud_functions', label: 'cloud_functions (GCP Functions)', matchBy: 'username' },
]

/**
 * The count a row contributes, picking the column that matches the action's
 * semantics: distinct objects for creations, history events for updates.
 *
 * `actionMeta` is passed in rather than read from a module constant, because
 * this module is now shared by ten reports whose actions differ. The registry
 * owns the definitions; this file owns the maths.
 */
export const rowCount = (row, actionMeta) => {
    const meta = actionMeta(row.action)
    if (!meta) return 0
    return Number(row[meta.metric]) || 0
}

/**
 * How many distinct records the given rows *updated*.
 *
 * A union rather than a sum, and the whole reason rows carry ids. `objectsCount`
 * is computed per day, so summing it double-counts anything touched on more than
 * one day; unioning ids collapses those days into one record. The union also
 * spans users, so a record two people edited counts once.
 *
 * Creation rows are skipped: every row carries ids, so without this filter the
 * answer silently becomes "records created or updated". Creations need no union
 * anyway -- an object is created once, so it cannot span days.
 *
 * Keyed by entity as well as id, because `objectId` is only unique within a
 * record type: incident 1 and agreement 1 are different records.
 */
export const countUniqueUpdatedObjects = (rows, actionMeta) => {
    const seen = new Set()
    rows.forEach((row) => {
        const meta = actionMeta(row.action)
        if (meta?.mode !== 'updated') return
        ;(row.objectIds || []).forEach((objectId) => seen.add(`${meta.entity}|${objectId}`))
    })
    return seen.size
}

/**
 * Narrow rows by any combination of action, branch, user, user id and
 * excluded user id.
 *
 * An empty list for a dimension means "no filter on it". Dimensions intersect,
 * so selecting a group and a user shows only that user, and only if they are in
 * the group.
 *
 * `userIds` is separate from `users` on purpose: the group filter resolves to
 * user ids, because display names are not unique and the report's name formula
 * does not match `UserNode.resolve_name` in every case. Ids are compared as
 * strings so a numeric row id and a GraphQL `pk` line up.
 *
 * `excludeUserIds` follows the same id-based convention as `userIds`, for the
 * same reason -- and for the same reason it is not `excludeUsers` matched by
 * name. It is a separate pass rather than folded into `userIds`: intersecting
 * (AND) is the right relationship between "must be one of these" and "must not
 * be one of these", and it is what makes exclusion win when a user appears in
 * both -- a row for that user still fails the exclude check even though it
 * passed the include check, so it drops out either way. A row with no actor at
 * all (`userId === null`, "System") has no real id to compare, so it is
 * matched via `SYSTEM_EXCLUDE_VALUE`, the sentinel the System option and the
 * default exclusion both use in place of an id.
 *
 * `excludeUsernames` is the one deliberate, documented exception to "match a
 * user by id, never by display name" -- see `DEFAULT_EXCLUDED_USERS` above.
 * It is not a display-name match: it compares the raw `auth_user.username`,
 * a stable identifier across environments, never `userName` (the resolved
 * display name BigQuery hands back, which is what the "never by name" rule
 * is actually protecting against -- see this module's other docstrings).
 * Kept as its own dimension rather than folded into `excludeUserIds` for the
 * same reason `excludeUserIds` is its own dimension rather than folded into
 * `userIds`: a clean intersecting pass, not another id shape smuggled through
 * a field that promises ids.
 */
export const filterRows = (
    rows,
    {
        actions = [],
        branches = [],
        users = [],
        userIds = [],
        excludeUserIds = [],
        excludeUsernames = [],
    } = {}
) => {
    const actionSet = actions.length ? new Set(actions) : null
    const branchSet = branches.length ? new Set(branches) : null
    const userSet = users.length ? new Set(users) : null
    const userIdSet = userIds.length ? new Set(userIds.map(String)) : null
    const excludeUserIdSet = excludeUserIds.length ? new Set(excludeUserIds.map(String)) : null
    const excludeUsernameSet = excludeUsernames.length ? new Set(excludeUsernames) : null

    return rows.filter((row) => {
        if (actionSet && !actionSet.has(row.action)) return false
        if (branchSet && !branchSet.has(row.branchName)) return false
        if (userSet && !userSet.has(row.userName)) return false
        if (userIdSet && !userIdSet.has(String(row.userId))) return false
        if (excludeUserIdSet) {
            const comparableId = row.userId === null ? SYSTEM_EXCLUDE_VALUE : String(row.userId)
            if (excludeUserIdSet.has(comparableId)) return false
        }
        if (excludeUsernameSet && row.username && excludeUsernameSet.has(row.username)) return false
        return true
    })
}

/** Sum `rowCount` into a plain object keyed by `keyFn`. */
export const totalsBy = (rows, keyFn, actionMeta) =>
    rows.reduce((acc, row) => {
        const key = keyFn(row)
        acc[key] = (acc[key] || 0) + rowCount(row, actionMeta)
        return acc
    }, {})

/** Keys of a totals object, largest first. */
export const rankKeys = (totals) => Object.keys(totals).sort((a, b) => totals[b] - totals[a])

export const toOptions = (values) => values.map((value) => ({ label: value, value }))

export const daysBetween = (startDate, endDate) => {
    const days = []
    const cursor = moment(startDate)
    const last = moment(endDate)
    while (cursor.isSameOrBefore(last, 'day')) {
        days.push(cursor.format('YYYY-MM-DD'))
        cursor.add(1, 'day')
        if (days.length >= 366) break
    }
    return days
}

/**
 * Users x day grid for the heatmap. Days with no activity are still emitted so
 * gaps in someone's week are visible rather than collapsed away.
 */
export const buildHeatmap = (rows, startDate, endDate, actionMeta) => {
    const days = daysBetween(startDate, endDate)

    const totals = totalsBy(rows, (row) => `${row.userName}|${row.eventDate}`, actionMeta)
    const userTotals = totalsBy(rows, (row) => row.userName, actionMeta)
    const users = rankKeys(userTotals)
    const peak = Math.max(0, ...Object.values(totals))

    const grid = users.map((userName) => ({
        userName,
        total: userTotals[userName],
        cells: days.map((day) => ({ day, count: totals[`${userName}|${day}`] || 0 })),
    }))

    return { days, grid, peak }
}

/**
 * Bucket a count onto a 0-6 step of the sequential blue ramp. Returned as a level
 * rather than a colour so the cell can wear a class instead of an inline style.
 */
export const heatLevel = (count, peak) => {
    if (!count) return 0
    if (peak <= 0) return 0
    return Math.max(1, Math.min(6, Math.ceil((count / peak) * 6)))
}

/**
 * Group rows by anything (user, branch) with one numeric field per series,
 * sorted by total descending.
 *
 * Records are keyed by `seriesKeyFn`, which defaults to the action name itself
 * so a chart series key *is* an action -- no second naming scheme to keep in
 * sync. The User Activity report overrides it to collapse thirty-odd actions
 * onto created/edited, which would otherwise be an unreadable legend.
 */
export const buildActionMix = (
    rows,
    { groupKeyFn, actions, groupKey = 'name', actionMeta, seriesKeyFn = DEFAULT_SERIES_KEY }
) => {
    const wanted = new Set(actions)
    const byGroup = {}

    rows.forEach((row) => {
        if (!wanted.has(row.action)) return
        const meta = actionMeta(row.action)
        const seriesKey = seriesKeyFn(row, meta)
        if (!seriesKey) return

        const key = groupKeyFn(row)
        if (!byGroup[key]) byGroup[key] = { [groupKey]: key, total: 0 }
        byGroup[key][seriesKey] = (byGroup[key][seriesKey] || 0) + rowCount(row, actionMeta)
        byGroup[key].total += rowCount(row, actionMeta)
    })

    return Object.values(byGroup).sort((a, b) => b.total - a.total)
}

/** One record per day with a field per series key, gaps included. */
export const buildActionTrend = (
    rows,
    startDate,
    endDate,
    actions,
    actionMeta,
    seriesKeyFn = DEFAULT_SERIES_KEY
) => {
    const days = daysBetween(startDate, endDate)
    const wanted = new Set(actions)
    const scoped = rows.filter((row) => wanted.has(row.action))

    // Series keys come from the actions in scope, not the rows that happened to
    // survive filtering -- an action with zero matching rows in this range still
    // gets its field on every record, defaulting to 0. Deriving keys from `scoped`
    // instead would drop that field to `undefined`, which a chart renders as a
    // gap (or throws on) rather than a flat zero line.
    const seriesKeys = new Set()
    actions.forEach((action) => {
        const seriesKey = seriesKeyFn({ action }, actionMeta(action))
        if (seriesKey) seriesKeys.add(seriesKey)
    })

    const totals = {}
    scoped.forEach((row) => {
        const seriesKey = seriesKeyFn(row, actionMeta(row.action))
        if (!seriesKey) return
        const key = `${row.eventDate}|${seriesKey}`
        totals[key] = (totals[key] || 0) + rowCount(row, actionMeta)
    })

    return days.map((day) => {
        const record = { day, label: moment(day).format('MMM D') }
        seriesKeys.forEach((seriesKey) => {
            record[seriesKey] = totals[`${day}|${seriesKey}`] || 0
        })
        return record
    })
}

/** Rows -> DataTable rows, given series whose keys index the record. */
export const buildMixTableRows = (data, series, groupKey = 'name') =>
    data.map((record) => ({
        name: record[groupKey],
        total: record.total || 0,
        values: series.reduce((acc, item) => ({ ...acc, [item.key]: record[item.key] || 0 }), {}),
    }))

/**
 * The figures a report's stat row shows. Generic on purpose -- these are the
 * only counts that mean the same thing on all ten reports.
 *
 * `headlineActions`, when given, scopes "created" and "edited" down to a
 * subset of the report's actions -- e.g. `incidents` excludes its party and
 * document actions, so "Incidents created" counts incidents, not documents
 * (see `headlineActionsFor` in reportRegistry.js). It is filtered in *before*
 * `countUniqueUpdatedObjects` runs its union, not applied to the union's
 * result -- summing per-day/per-user counts after the fact would reintroduce
 * exactly the double-counting that function exists to avoid. Omitting
 * `headlineActions` scopes to every action in the report, which is the
 * eight single-record-type reports' default.
 *
 * "Total edit events" (`editEvents`) is deliberately *not* scoped by
 * `headlineActions` -- it is the raw update-event count across every action
 * in the report (i.e. every action `rows` carries), so it stays larger than
 * `edited` whenever a report has non-headline actions with their own update
 * events (documents, parties, insurance removals).
 */
export const buildKpis = (rows, startDate, endDate, actionMeta, headlineActions) => {
    const headlineSet = headlineActions ? new Set(headlineActions) : null
    const headlineRows = headlineSet ? rows.filter((row) => headlineSet.has(row.action)) : rows

    const created = headlineRows.filter((row) => actionMeta(row.action)?.mode === 'created')
    const headlineUpdated = headlineRows.filter((row) => actionMeta(row.action)?.mode === 'updated')
    const allUpdated = rows.filter((row) => actionMeta(row.action)?.mode === 'updated')

    return {
        created: created.reduce((total, row) => total + rowCount(row, actionMeta), 0),
        // Distinct records edited, scoped to the headline actions -- unions
        // ids across days and users (see `countUniqueUpdatedObjects`), so a
        // record two people edited on two different days still counts once.
        edited: countUniqueUpdatedObjects(headlineUpdated, actionMeta),
        // The raw event total, unscoped -- every update event this report
        // logs, across every record type it tracks.
        editEvents: allUpdated.reduce((total, row) => total + rowCount(row, actionMeta), 0),
        // By id, not name -- display names are not unique (see filterRows above),
        // so two people sharing a name would otherwise merge into one row here.
        // Null ids (rows with no actor at all -- "System") are excluded: System
        // is automation, not a person, and counting it would inflate "Staff
        // active" with something nobody did. A set user_id with no auth_user
        // match (a mobile driver, or a deleted user) is still a real person and
        // stays counted -- only the true no-actor population is dropped here.
        staff: new Set(rows.map((row) => row.userId).filter((userId) => userId !== null)).size,
    }
}

/**
 * Value for a report-declared "extra" stat tile (see `report.extraTiles` in
 * reportRegistry.js -- e.g. incidents' "Documents uploaded"). Sums `rowCount`
 * over just the tile's own `actions`, the same per-row metric selection every
 * other tile already uses: a created-mode action reads `objectsCount`, an
 * updated-mode one reads `eventsCount` (see `rowCount`). For a tile scoped to
 * a single created-mode action -- the only kind so far -- that sum is already
 * a distinct-object count with no union needed, exactly like "{noun} created"
 * above: an object is created once, so `objectsCount` can't double-count it
 * across days the way an update event can.
 */
export const buildExtraTileValue = (rows, actionMeta, actions) => {
    const wanted = new Set(actions)
    return rows
        .filter((row) => wanted.has(row.action))
        .reduce((total, row) => total + rowCount(row, actionMeta), 0)
}

/**
 * Map a palette hex back to its slot number so DOM elements (legend and tooltip
 * swatches) can wear a `viz-swatch--n` class instead of an inline background.
 *
 * Recharts hands us the resolved fill colour in its tooltip payload, and every
 * colour we ever pass in comes from PALETTE or OTHER_COLOR, so this is total.
 */
export const slotForColor = (color) => {
    const index = PALETTE.indexOf(color)
    return index === -1 ? 'other' : index + 1
}

/* -------------------------------------------------------------------------
   Users view
   -------------------------------------------------------------------------
   Every report's second chart layout: who did the work, not what it was on.
   Generic on purpose, exactly like the maths above -- one leaderboard/heatmap
   /trend layout serves all nine reports, scoped by whichever rows and
   `actionMeta` the caller passes in.
   ------------------------------------------------------------------------- */

/** CONSTANT_CASE entity value -> a readable label, e.g. 'PARTY_VEHICLE' ->
 * 'Party Vehicle'. There is no separate entity-name table any more (the old
 * `ENTITIES` constant this replaces was Incidents-specific); every action
 * already carries its `entity`, so this just formats it. */
export const entityLabel = (entityValue) =>
    entityValue
        .split('_')
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ')

/**
 * Leaderboard shaping: one record per user with a flat `created_ENTITY` /
 * `updated_ENTITY` field per entity (the record type an action operates on --
 * see `entity` on each action in the registry), sorted by total effort
 * descending.
 *
 * `actionMeta` is a lookup, not a list, like every other function in this
 * file, so there is no separate entity enumeration to keep in sync with the
 * registry -- the entity legend is built up from the rows actually visited.
 *
 * `entityColor` is a second lookup, entity -> colour (see
 * `entityColorFor` in `reportRegistry.js`), kept separate from `actionMeta`
 * because it answers a different question: an action's own `.color`
 * distinguishes created vs updated *within* one entity, but the leaderboard
 * stacks by entity, so every action on the same entity has to render in the
 * same colour here regardless of which one a row happens to be.
 */
export const buildLeaderboard = (rows, actionMeta, entityColor) => {
    const byUser = {}
    const entityInfo = {}

    rows.forEach((row) => {
        const meta = actionMeta(row.action)
        if (!meta) return

        if (!byUser[row.userName]) byUser[row.userName] = { userName: row.userName, total: 0 }
        const field = `${meta.mode}_${meta.entity}`
        byUser[row.userName][field] = (byUser[row.userName][field] || 0) + rowCount(row, actionMeta)
        byUser[row.userName].total += rowCount(row, actionMeta)

        if (!entityInfo[meta.entity]) {
            entityInfo[meta.entity] = { label: entityLabel(meta.entity), color: entityColor(meta.entity) }
        }
    })

    const data = Object.values(byUser).sort((a, b) => b.total - a.total)
    const series = Object.keys(entityInfo).map((entityValue) => ({
        key: entityValue,
        label: entityInfo[entityValue].label,
        color: entityInfo[entityValue].color,
    }))

    return { data, series }
}

/** DataTable rows for the leaderboard, split into created/updated columns. */
export const buildLeaderboardTable = (data, series) => {
    const columns = series.flatMap((entity) => [
        { key: `created_${entity.key}`, label: `${entity.label} created`, color: entity.color },
        { key: `updated_${entity.key}`, label: `${entity.label} updated`, color: entity.color },
    ])

    const rows = data.map((record) => ({
        name: record.userName,
        total: record.total || 0,
        values: columns.reduce((acc, column) => ({ ...acc, [column.key]: record[column.key] || 0 }), {}),
    }))

    return { rows, columns }
}

/**
 * The richer, people-focused KPI set the old Incidents Users tab showed:
 * total actions, active users, the busiest day, and who did the most. Kept
 * separate from `buildKpis` above rather than folded into it -- the two
 * answer different questions ("how much module activity happened" vs "who
 * did it"), and nine reports' module view depends on `buildKpis`'s current
 * shape, which this leaves untouched.
 */
export const buildUserKpis = (rows, startDate, endDate, actionMeta) => {
    const total = rows.reduce((sum, row) => sum + rowCount(row, actionMeta), 0)
    const byUser = totalsBy(rows, (row) => row.userName, actionMeta)
    const byDay = totalsBy(rows, (row) => row.eventDate, actionMeta)

    // "Most active user" is scoped to a separate totals set, excluding System
    // (null userId) rows, the same population "Staff active" excludes in
    // `buildKpis` above: System is automation, not a person, and must never
    // win this figure just because cron ran more often than anyone worked.
    // Kept separate from `byUser` so `activeUsers` below still reflects
    // everyone -- System included -- which is a different question ("how many
    // distinct actors" vs "who is the top person").
    const humanByUser = totalsBy(
        rows.filter((row) => row.userId !== null),
        (row) => row.userName,
        actionMeta
    )
    const topUser = rankKeys(humanByUser)[0]
    const busiestDay = rankKeys(byDay)[0]

    return {
        totalSpark: startDate && endDate ? daysBetween(startDate, endDate).map((day) => byDay[day] || 0) : [],
        totalActions: total,
        activeUsers: Object.keys(byUser).length,
        busiestDay: busiestDay ? moment(busiestDay).format('MMM D') : '--',
        busiestDayCount: busiestDay ? byDay[busiestDay] : 0,
        topUser: topUser || '--',
        topUserCount: topUser ? humanByUser[topUser] : 0,
    }
}
